import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppStore } from '../../../redux/hooks';
import { addAppliedJob, updateJobApplicationState, updateJobHiddenState, type ApplicationState } from '../../../redux/features/jobs/jobsSlice';
import { useSavedJobActions } from '../../../hooks/useSavedJobActions';
import { useUserJobStateSync } from '../../../hooks/useUserJobStateSync';
import { trackJobSave, trackJobUnsave, trackJobApply } from '../../../utils/analytics/analytics';
import { APPLICATION_STATES } from '../../application-state';
import type { DisplayJob } from '../../../types';

export interface UseHitActionsProps {
  hit: DisplayJob;
  jobId: string;
  isSaved: boolean;
  isViewed: boolean;
  isApplied: boolean;
  isHidden: boolean;
  savedJobPayload: DisplayJob & { id: string };
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void;
  onMenuClose?: () => void;
}

export interface HitActions {
  handleToggleSaved: () => Promise<void>;
  handleMarkViewed: () => Promise<void>;
  handleToggleApplied: () => Promise<void>;
  handleToggleHide: () => void;
  handleSetApplicationState: (state: ApplicationState) => Promise<void>;
}

/**
 * Hook to manage all actions for a Hit component
 * Handles save, unsave, apply, hide, and application state changes
 */
export function useHitActions({
  hit,
  jobId,
  isSaved,
  isViewed,
  isApplied,
  isHidden,
  savedJobPayload,
  showToast,
  onMenuClose,
}: UseHitActionsProps): HitActions {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { saveJob, unsaveJob } = useSavedJobActions();
  const { recordViewedJob, setAppliedJobState, syncApplicationStateToBackend } = useUserJobStateSync();

  const handleToggleSaved = useCallback(async () => {
    if (!isSaved) {
      trackJobSave({
        jobId: hit.objectID,
        jobTitle: hit.normalisedTitle || hit.title || '',
        locale: intl.locale,
      });
      const success = await saveJob(savedJobPayload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.saved', defaultMessage: 'Job saved', description: 'Success toast notification message shown when a job is saved' }),
          'success'
        );
      }
    } else {
      trackJobUnsave({
        jobId: hit.objectID,
        jobTitle: hit.normalisedTitle || hit.title || '',
        locale: intl.locale,
      });
      const success = await unsaveJob(savedJobPayload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.unsaved', defaultMessage: 'Job removed from saved', description: 'Info toast notification message shown when a job is removed from saved jobs' }),
          'info'
        );
      }
    }
  }, [hit, intl, isSaved, saveJob, savedJobPayload, unsaveJob, showToast]);

  const handleMarkViewed = useCallback(async () => {
    if (!isViewed) {
      const success = await recordViewedJob(savedJobPayload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.viewed', defaultMessage: 'Marked as viewed', description: 'Success toast notification message shown when a job is marked as viewed' }),
          'success'
        );
      }
    }
  }, [isViewed, recordViewedJob, savedJobPayload, intl, showToast]);

  const handleToggleApplied = useCallback(async () => {
    const willBeApplied = !isApplied;
    const success = await setAppliedJobState(savedJobPayload, willBeApplied);
    if (success) {
      if (willBeApplied) {
        trackJobApply({
          jobId: hit.objectID,
          jobTitle: hit.normalisedTitle || hit.title || '',
          companyId: hit.companyId || '',
          locale: intl.locale,
        });
        showToast(
          intl.formatMessage({ id: 'hit.toast.applied', defaultMessage: 'Marked as applied', description: 'Success toast notification message shown when a job is marked as applied to' }),
          'success'
        );
      } else {
        showToast(
          intl.formatMessage({ id: 'hit.toast.unapplied', defaultMessage: 'Removed from applied', description: 'Info toast notification message shown when applied status is removed from a job' }),
          'info'
        );
      }
    }
  }, [isApplied, setAppliedJobState, savedJobPayload, hit, intl, showToast]);

  const handleToggleHide = useCallback(() => {
    const willBeHidden = !isHidden;
    dispatch(updateJobHiddenState({ jobId, isHidden: willBeHidden }));
    
    // Validate state was updated by checking Redux state
    // Use setTimeout to allow Redux state to update
    setTimeout(() => {
      const state = store.getState();
      const allJobs = [...state.jobs.savedJobs, ...state.jobs.viewedJobs, ...state.jobs.appliedJobs];
      const job = allJobs.find((j) => j.id === jobId);
      const hasCorrectState = job?.isHidden === willBeHidden;
      
      if (hasCorrectState) {
        showToast(
          willBeHidden
            ? intl.formatMessage({ id: 'hit.toast.hidden', defaultMessage: 'Job hidden', description: 'Info toast notification message shown when a job is hidden' })
            : intl.formatMessage({ id: 'hit.toast.unhidden', defaultMessage: 'Job unhidden', description: 'Success toast notification message shown when a previously hidden job is unhidden' }),
          willBeHidden ? 'info' : 'success'
        );
      }
    }, 0);
  }, [isHidden, dispatch, jobId, intl, showToast, store]);

  const handleSetApplicationState = useCallback(async (state: ApplicationState) => {
    // Job may be "applied" only in SavedStateContext (e.g. SRP island) and not in Redux.
    // updateJobApplicationState only updates jobs already in Redux, so ensure the job is in
    // appliedJobs first; then sync will see correct local state and succeed.
    const inApplied = store.getState().jobs.appliedJobs.some((j) => j.id === jobId);
    if (!inApplied) {
      dispatch(
        addAppliedJob({
          ...savedJobPayload,
          id: jobId,
          applicationState: state,
          appliedAt: new Date().toISOString(),
        } as Parameters<typeof addAppliedJob>[0])
      );
    } else {
      dispatch(updateJobApplicationState({ jobId, applicationState: state }));
    }

    const success = await syncApplicationStateToBackend(jobId, state, savedJobPayload);

    if (success) {
      if (state !== null && !isApplied) {
        trackJobApply({
          jobId: hit.objectID,
          jobTitle: hit.normalisedTitle || hit.title || '',
          companyId: hit.companyId || '',
          locale: intl.locale,
        });
      }

      const stateConfig = APPLICATION_STATES.find((s) => s.value === state);
      if (stateConfig) {
        showToast(
          intl.formatMessage(
            { id: 'tools.applicationState.updated', defaultMessage: 'State updated to {state}' },
            { state: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
          ),
          'success'
        );
      }
    }
    onMenuClose?.();
  }, [dispatch, store, jobId, intl, showToast, isApplied, syncApplicationStateToBackend, savedJobPayload, hit, onMenuClose]);

  return {
    handleToggleSaved,
    handleMarkViewed,
    handleToggleApplied,
    handleToggleHide,
    handleSetApplicationState,
  };
}
